import { Box, Card, CardContent, Chip, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { EmptyContent, FullPageSpinner, getCurrencySymbol } from '@zeta-core';
import { InfoIconOutlined } from '@zeta-core/assets';
import { getErrorMessage, InvestorService } from '@zeta-services';
import {
  IBusinessOwnerDetails,
  IClientCardItem,
  IFactFindEntity,
  IGenericErrorResponse,
  IGetBusinessOwnershipDetailsResponse,
  IStakeHolder,
} from '@zeta-types';

const BusinessOwnership = () => {
  const location = useLocation();
  const theme = useTheme();
  const state = (location.state || {}) as { entityData: IFactFindEntity; clientData: IClientCardItem };
  const clientID = state?.clientData?.clientID;

  const [businessDetails, setBusinessDetails] = useState<IBusinessOwnerDetails>([]);
  const [owners, setOwners] = useState<IStakeHolder[]>([]);

  const getOwnersDetail = useMutation({
    mutationFn: (payload: { clientID: string }) => InvestorService().getBusinessOwnershipDetails(payload),
    onSuccess: (response: AxiosResponse<IGetBusinessOwnershipDetailsResponse>) => {
      setBusinessDetails(response?.data?.businessClientDetails);
      setOwners(response?.data?.stakeHoldersDetails);
    },
    onError: (error: IGenericErrorResponse) => {
      const errorCode = error?.response?.data?.error?.errorCode;
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  useEffect(() => {
    if (clientID) getOwnersDetail.mutateAsync({ clientID: clientID });
  }, [getOwnersDetail.mutateAsync, clientID]);

  return (
    <>
      {getOwnersDetail.isPending && <FullPageSpinner />}
      {owners?.length > 0 && !getOwnersDetail.isPending && (
        <Card sx={{ py: 8, px: 3, height: '50vh', marginBottom: 20, background: theme.palette.grey[100] }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Paper
              sx={{
                position: 'relative',
                mb: '39px',
                borderRadius: 2,
                py: 2,
                px: 3,
                pb: '16px !important',
                textAlign: 'center',
                minWidth: '250px',
                boxShadow: '0px 20px 40px -4px #919EAB29',
              }}
            >
              <Typography variant="subtitle1">{businessDetails?.businessName ?? '--'}</Typography>
              <Typography variant="subtitle1">{businessDetails?.registrationNumber ?? '--'}</Typography>

              <Tooltip
                title={
                  <Stack>
                    <Typography variant="caption">Business Type: {businessDetails?.businessType ?? '--'}</Typography>
                    <Typography variant="caption">Incorporation Date: {dayjs(businessDetails?.incorporationDate).format('DD-MM-YYYY') ?? '--'}</Typography>
                  </Stack>
                }
              >
                <Stack sx={{ position: 'absolute', right: theme.spacing(1), top: theme.spacing(1) }}>
                  <InfoIconOutlined />
                </Stack>
              </Tooltip>

              <Box
                sx={{
                  position: 'absolute',
                  width: '2px',
                  height: '40px',
                  background: '#aeb7be',
                  left: '50%',
                  bottom: '-40px',
                  transform: 'translateX(-50%)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-2px',
                    left: '50%',
                    width: '6px',
                    height: '6px',
                    backgroundColor: '#aeb7be',
                    borderRadius: '50%',
                    transform: 'translateX(-50%)',
                  },
                }}
              ></Box>
            </Paper>
          </Box>

          <Box
            sx={{
              position: 'relative',
              margin: '0 auto',
              borderTop: owners.length > 1 ? '2px solid' : 'none',
              borderImage: 'linear-gradient(to right, transparent 130px, #aeb7be 100px, #aeb7be calc(100% - 130px), transparent calc(100% - 130px)) 1',
              width: owners.length > 5 ? '100%' : 'fit-content',
              height: 'auto',
              overflowX: 'auto',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: owners.length > 5 ? 'space-between' : 'center',
                marginTop: '42px',
                gap: '40px',
                position: 'relative',
                width: '100%',
              }}
            >
              {owners.map((e, i) => {
                const total = owners.length;
                const isLeft = i < Math.floor(total / 2);
                const isRight = i >= Math.floor(total / 2);
                const isCenter = total % 2 !== 0 && i === Math.floor(total / 2);

                return (
                  <Card
                    sx={{
                      position: 'relative',
                      mb: 5,
                      overflow: 'visible',
                      minWidth: '250px',
                      '&::before': {
                        content: '""',
                        width: '10px',
                        height: '50px',
                        position: 'absolute',
                        top: '-45px',
                        left: '50.5%',
                        transform: 'translateX(-50%)',
                        background: `transparent url("/arrow.svg") no-repeat`,
                      },
                      ...(!isCenter && {
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: '-45px',
                          left: isRight ? 'calc(50% - 11px)' : 'calc(50% - 1px)',
                          width: '12px',
                          height: '12px',
                          background: '#ffffff',
                          border: '2px solid #aeb7be',
                          borderBottom: 'none',
                          borderLeft: isRight && 'none',
                          borderTopRightRadius: isRight && '50%',
                          borderRight: isLeft && 'none',
                          borderTopLeftRadius: isLeft && '50%',
                          zIndex: 2,
                        },
                      }),
                    }}
                  >
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, textAlign: 'center', p: 2 }}>
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant="subtitle1">{e?.name ?? '--'}</Typography>
                          <Typography variant="body2" color="primary">
                            {e?.titleOrRole ?? '--'}
                          </Typography>
                        </Box>

                        <Stack spacing={0.5}>
                          <Chip label={`Ownership - ${e?.ownershipPercentage}`} size="small" sx={{ width: 'fit-content' }} />
                          <Typography variant="subtitle2">
                            {getCurrencySymbol(e?.valuationOfShares.split(' ')[0] ?? 'GBP')} {e?.valuationOfShares.split(' ')[1]} Valuation
                          </Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Box>
        </Card>
      )}

      {(!owners || owners?.length === 0) && !getOwnersDetail.isPending && (
        <Card sx={{ py: 8, px: 3, height: '50vh', marginBottom: 20, background: theme.palette.grey[100] }}>
          <EmptyContent title="No Data Available" />
        </Card>
      )}
    </>
  );
};

export default BusinessOwnership;
